import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { DatePipe, NgIf } from '@angular/common';
import {
  FeaturesComponent,
  ProjectComponent,
} from '@nx-massaud/detail/ui/project';
import {
  NodeProject,
  Project,
  UiProjectGridItem,
} from '@madeinlune/massaud-types';
import { ComponentStore } from '@ngrx/component-store';
import { LetDirective } from '@ngrx/component';
import { combineLatest, delay, map, Observable, shareReplay, tap } from 'rxjs';
import {
  EntitiesStore,
  EntityStore,
} from '@madeinlune/massaud-json-api-bridge';
import { ActivatedRoute } from '@angular/router';
import {
  projectAllListQuery,
  projectDetailQuery,
} from '@madeinlune/massaud-query-builder';
import { CoverComponent } from '@nx-massaud/detail/ui/cover';
import { isEqual } from '@madeinlune/ngx-operators';
import { TranslocoModule } from '@ngneat/transloco';
import { LoadingDirective } from '@nx-massaud/loading';
import { AssociatedProjectsComponent } from '@nx-massaud/ui/associated-projects';

const initialState = { associated: [], media: [] };

export class AllProjectsStore extends EntitiesStore<UiProjectGridItem> {}

@Component({
  selector: 'msd-det-deat-project-feature',
  standalone: true,
  template: `
    <ng-container *ngrxLet="vm$; let vm">
      <ng-container
        *i2lLoading="vm.loading; errorMessage: vm.error"
      ></ng-container>
      <ng-container *ngIf="!vm.loading">
        <ng-container *ngIf="vm.project as project">
          <msd-det-ui-project
            [project]="project"
            [pressKit]="project.presskit"
            [awards]="project.awards"
            [paragraphs]="project.paragraphs"
          >
            <msd-det-ui-cover
              class="media-gallery"
              [media]="project.cover"
            ></msd-det-ui-cover>
            <msd-det-ui-features
              class="features"
              [typologie]="project.firstTypology?.parent"
              [subTypologie]="project.firstTypology"
              [year]="project?.date | date : 'yyyy'"
              [partners]="project.partners"
            >
            </msd-det-ui-features>
            <div class="associated">
              <msd-ui-associated-projects
                [projects]="vm.randomProjects"
              ></msd-ui-associated-projects>
            </div>
          </msd-det-ui-project>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./project-feature.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,
    FeaturesComponent,
    CoverComponent,
    TranslocoModule,
    LoadingDirective,
    DatePipe,
    NgIf,
    AssociatedProjectsComponent,
    ProjectComponent,
  ],
  providers: [EntityStore, AllProjectsStore],
})
export class ProjectFeatureComponent
  extends ComponentStore<any>
  implements OnInit
{
  allProjectsStore = inject(AllProjectsStore);
  projectId$ = inject(ActivatedRoute).params.pipe(
    map((params) => params?.['projectId'])
  );

  private readonly project$: Observable<NodeProject | undefined> =
    this.projectStore.entity$.pipe(isEqual(), delay(100), shareReplay(1));
  randomProjects$ = combineLatest([
    this.allProjectsStore.entities$,
    this.project$,
  ]).pipe(
    map(([projects, project]) => {
      if (projects && project) {
        const projectsWithoutCurrent = projects.filter(
          (p) => p.id !== project.id
        );
        return projectsWithoutCurrent;
      }
      return [];
    })
  );

  private readonly projectLoading$: Observable<boolean> =
    this.projectStore.loading$;
  private readonly projectErrorMessage$: Observable<string | undefined> =
    this.projectStore.errorMessage$;

  readonly vm$ = this.select({
    project: this.project$,
    loading: this.projectLoading$,
    error: this.projectErrorMessage$,
    randomProjects: this.randomProjects$,
  });

  setEntityId = this.effect(() => {
    return this.projectId$.pipe(
      tap((entityId: string | null) => {
        if (entityId) {
          this.projectStore.load({ query: projectDetailQuery, entityId });
        }
      })
    );
  });

  constructor(private projectStore: EntityStore<Project>) {
    super(initialState);
    this.allProjectsStore.modelClass = UiProjectGridItem;
    this.allProjectsStore.load({ query: projectAllListQuery });
  }

  ngOnInit(): void {}
}
